import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { MapPin, DollarSign, Zap, Star, Lock, Crown, Crosshair, Shield, ChevronDown, Flame, Banknote, Share2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { getCoords, ERA_MAP_CONFIG } from '../utils/mapCoordinates';
import { getTerritoryImage } from '../data/territoryImages';

const CARTO_KEY = process.env.REACT_APP_CARTO_KEY || '';
const DARK_TILES = CARTO_KEY
  ? `https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`
  : 'https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png';
const DARK_TILES_ATTR = '&copy; OpenStreetMap &copy; CARTO';

// Zoom tracker
const ZoomTracker = ({ onZoomChange }) => {
  useMapEvents({ zoomend: (e) => onZoomChange(e.target.getZoom()) });
  return null;
};

// Fly to location
const FlyTo = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 0.6 });
  }, [center, zoom, map]);
  return null;
};

// Auto-zoom controller for legend filters (TRIB → Clinton Hill)
const FilterZoomController = ({ legendFilter, showLegends, eraConfig }) => {
  const map = useMap();
  const prevFilter = useRef(legendFilter);
  useEffect(() => {
    if (prevFilter.current === legendFilter) return;
    prevFilter.current = legendFilter;
    if (!showLegends) return;
    if (legendFilter === 'tribute') {
      const tributes = LEGENDS_DATA.filter(l => l.category === 'tribute');
      if (tributes.length > 0) {
        const bounds = L.latLngBounds(tributes.map(l => [l.lat, l.lng]));
        map.flyToBounds(bounds, { padding: [50, 50], duration: 0.8, maxZoom: 16 });
      }
    } else {
      map.flyTo(eraConfig.center, eraConfig.zoom, { duration: 0.6 });
    }
  }, [legendFilter, showLegends, eraConfig, map]);
  return null;
};

// Color schemes
const JOB_COLORS = {
  story: { fill: '#C9A84C', stroke: '#FFD700', glow: '#C9A84C40' },
  common: { fill: '#4A7AB5', stroke: '#6B9BD2', glow: '#4A7AB530' },
  uncommon: { fill: '#9B59B6', stroke: '#BB77D6', glow: '#9B59B630' },
  rare: { fill: '#E8445A', stroke: '#FF6B81', glow: '#E8445A40' },
  epic: { fill: '#FFD700', stroke: '#FFED4A', glow: '#FFD70040' },
  boss: { fill: '#8B1A1A', stroke: '#CC3333', glow: '#8B1A1A50' },
  locked: { fill: '#333', stroke: '#555', glow: '#33333330' },
  completed: { fill: '#1A2535', stroke: '#2A3545', glow: '#1A253520' },
};

const TERRITORY_COLORS = {
  owned: { fill: '#4ADE80', stroke: '#22C55E' },
  available: { fill: '#4A7AB5', stroke: '#6B9BD2' },
  boss: { fill: '#8B1A1A', stroke: '#CC3333' },
  locked: { fill: '#555', stroke: '#777' },
  boss_owned: { fill: '#22D3EE', stroke: '#67E8F9' },
};

const getJobColor = (job, isStory) => {
  if (job.completed) return JOB_COLORS.completed;
  if (!job.level_ok) return JOB_COLORS.locked;
  if (job.special === 'boss_confrontation' || job.special === 'respect_boost') return JOB_COLORS.boss;
  if (isStory) return JOB_COLORS.story;
  if (job.tier === 'rare') return JOB_COLORS.rare;
  if (job.tier === 'uncommon') return JOB_COLORS.uncommon;
  if (job.tier === 'epic') return JOB_COLORS.epic;
  return JOB_COLORS.common;
};

const getTerritoryColor = (t) => {
  if (t.owned && t.is_boss_block) return TERRITORY_COLORS.boss_owned;
  if (t.owned) return TERRITORY_COLORS.owned;
  if (t.dev_status === 'in_development') return TERRITORY_COLORS.locked;
  if (t.is_boss_block) return TERRITORY_COLORS.boss;
  return TERRITORY_COLORS.available;
};

const getRadius = (zoom, isImportant) => {
  const base = isImportant ? 7 : 5;
  if (zoom >= 15) return base * 1.6;
  if (zoom >= 13) return base * 1.2;
  if (zoom >= 11) return base * 1;
  return base;
};

// =============================================
// JOB DETAIL PANEL
// =============================================
const JobDetailPanel = ({ job, isStory, onDoJob, onClose, loading }) => {
  if (!job) return null;
  const colors = getJobColor(job, isStory);
  const canDo = job.can_afford && job.level_ok && job.has_energy && !job.completed;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      className="absolute bottom-0 left-0 right-0 z-[1000] rounded-t-xl"
      style={{ background: 'linear-gradient(to top, #0a0c10f5, #0a0c10e0)', backdropFilter: 'blur(12px)', borderTop: `1px solid ${colors.fill}30` }}
    >
      <div className="max-w-lg mx-auto p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded text-[8px] font-bold tracking-widest font-['Outfit']"
                style={{ background: `${colors.fill}15`, color: colors.fill, border: `1px solid ${colors.fill}30` }}>
                {job.completed ? 'DONE' : isStory ? 'STORY' : (job.tier || 'HUSTLE').toUpperCase()}
              </span>
              {job.special && <Flame size={12} style={{ color: colors.fill }} />}
            </div>
            <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{job.name}</h4>
            {job.desc && <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mt-1 italic">{job.desc}</p>}
          </div>
          <button onClick={onClose} className="text-[#E8E0D0]/20 hover:text-[#E8E0D0] p-1" data-testid="job-detail-close">
            <ChevronDown size={18} />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">REWARD</p>
            <p className="font-['Outfit'] text-sm text-[#4ADE80] font-bold">${(job.reward || 0).toLocaleString()}</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">XP</p>
            <p className="font-['Outfit'] text-sm text-[#4A7AB5] font-bold">+{job.xp}</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">COST</p>
            <p className="font-['Outfit'] text-sm text-[#E8E0D0]/60 font-bold">${(job.cost || 0).toLocaleString()}</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">ENERGY</p>
            <p className="font-['Outfit'] text-sm text-[#E8E0D0]/60 font-bold">{job.energy_cost}E</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[#E8E0D0]/30 text-[10px] font-['Outfit']">
          <span>Success: {job.success_base}%</span>
          {!job.level_ok && <span className="text-[#8B1A1A]">Requires Lv.{job.min_level}</span>}
          {!job.can_afford && job.level_ok && <span className="text-[#8B1A1A]">Can't afford</span>}
          {!job.has_energy && job.level_ok && job.can_afford && <span className="text-[#8B1A1A]">No energy</span>}
        </div>
        {canDo && (
          <button
            onClick={() => onDoJob(job.id)}
            disabled={loading}
            className="w-full py-3 rounded-lg font-['Outfit'] text-xs tracking-widest font-bold transition-all hover:brightness-125 disabled:opacity-30"
            style={{ background: `${colors.fill}15`, border: `1px solid ${colors.fill}40`, color: colors.fill }}
            data-testid="job-do-btn"
          >
            {loading ? '...' : job.special === 'boss_confrontation' ? 'STRIKE' : isStory ? 'DO JOB' : 'HUSTLE'}
          </button>
        )}
      </div>
    </motion.div>
  );
};

// =============================================
// TERRITORY DETAIL PANEL
// =============================================
const TerritoryDetailPanel = ({ territory, onClaim, onCollect, onClose, loading }) => {
  if (!territory) return null;
  const colors = getTerritoryColor(territory);
  const terrImg = getTerritoryImage(territory);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      className="absolute bottom-0 left-0 right-0 z-[1000] rounded-t-xl"
      style={{ background: 'linear-gradient(to top, #0a0c10f5, #0a0c10e0)', backdropFilter: 'blur(12px)', borderTop: `1px solid ${colors.fill}30` }}
    >
      {/* Territory image banner */}
      <div className="relative h-24 overflow-hidden rounded-t-xl pointer-events-none">
        <img src={terrImg} alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c10] via-[#0a0c10]/70 to-transparent" />
      </div>
      <div className="max-w-lg mx-auto p-4 space-y-3 -mt-8 relative z-10">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded text-[8px] font-bold tracking-widest font-['Outfit']"
                style={{ background: `${colors.fill}15`, color: colors.fill, border: `1px solid ${colors.fill}30` }}>
                {territory.owned ? 'OWNED' : territory.is_boss_block ? 'BOSS BLOCK' : territory.dev_status === 'in_development' ? 'LOCKED' : 'UNCLAIMED'}
              </span>
              {territory.is_boss_block && <Crown size={12} className="text-[#FFD700]" />}
            </div>
            <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{territory.name}</h4>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">{territory.tier_label}</p>
          </div>
          <button onClick={onClose} className="text-[#E8E0D0]/20 hover:text-[#E8E0D0] p-1" data-testid="territory-detail-close">
            <ChevronDown size={18} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">INCOME</p>
            <p className="font-['Outfit'] text-sm text-[#4ADE80] font-bold">${territory.cash_per_cycle?.toLocaleString()}/cycle</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">XP</p>
            <p className="font-['Outfit'] text-sm text-[#4A7AB5] font-bold">+{territory.xp_per_cycle}/cycle</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">COST</p>
            <p className="font-['Outfit'] text-sm text-[#E8E0D0]/60 font-bold">${territory.cost?.toLocaleString()}</p>
          </div>
        </div>
        {territory.dev_status === 'in_development' && (
          <div className="bg-[#333]/20 border border-[#555]/30 rounded-lg px-3 py-2">
            <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40">Requires Level {territory.min_level} to unlock</p>
          </div>
        )}
        {territory.assigned_crew && Object.keys(territory.assigned_crew).length > 0 && (
          <div className="bg-[#C9A84C]/5 border border-[#C9A84C]/15 rounded-lg px-3 py-2">
            <p className="font-['Outfit'] text-[9px] text-[#C9A84C]/60">Crew assigned: {Object.keys(territory.assigned_crew).length} members</p>
          </div>
        )}
        <div className="flex gap-2">
          {!territory.owned && territory.can_afford && territory.dev_status !== 'in_development' && (
            <button onClick={() => onClaim(territory.id)} disabled={loading}
              className="flex-1 py-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30"
              data-testid="territory-claim-btn">
              {loading ? '...' : `CLAIM — $${territory.cost?.toLocaleString()}`}
            </button>
          )}
          {territory.owned && (
            <button onClick={() => onCollect(territory.id)} disabled={loading}
              className="flex-1 py-3 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-lg text-[#4ADE80] font-['Outfit'] text-xs tracking-widest hover:bg-[#4ADE80]/20 disabled:opacity-30"
              data-testid="territory-collect-btn">
              {loading ? '...' : 'COLLECT'}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// =============================================
// LEGEND OVERLAY
// =============================================

// ─── LEGENDS DATA — Clickable map landmarks ───
const MATT_PHOTOS = {
  netsJacket: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/yteqrdui_1406F613-F3C1-4442-AA6D-FBBFFFD4CCCF.png',
  thinker: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/39vhosw3_45DB35AB-E507-4CD6-96AE-1EDD8FE2A0E9.png',
  crown: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/mbe8xj7u_8CAAA203-4BA4-4157-9FD2-00637A4795DF.png',
  whiteSuit: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/m8sestwl_20F2936B-4E5B-4109-8CD6-4FB27B876078.png',
  clintonHill: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/khhqur42_7CD68BF9-989D-41F7-932E-DB1086B58E5A.png',
  longLiveMatt: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/yuhx9fmf_321BB7A2-4ADD-4781-8CED-DE5121F204F5.png',
};

const LEGENDS_DATA = [
  // ─── BACKPACK MATT TRIBUTES (always visible, no era filter) ───
  {
    id: 'matt_tribute_1',
    category: 'tribute',
    name: 'Backpack Matt — Bainbridge & Fulton',
    subtitle: 'His Block. His Legacy.',
    description: 'This corner belonged to Matt before any of us knew what legacy meant. He walked these blocks every single day — backpack on, head up, always moving with purpose. He gave this neighborhood something to believe in. Not with speeches. With presence. MATTHEW EZEKIEL FRANKLIN. 1987-2025. Clinton Hill will never forget you.',
    image: MATT_PHOTOS.netsJacket,
    lat: 40.6830, lng: -73.9618,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_2',
    category: 'tribute',
    name: 'Backpack Matt — The Thinker',
    subtitle: 'Always Three Moves Ahead',
    description: 'Matt never moved without thinking first. While everybody else was reacting, he was already five steps ahead — quiet, calculated, always with a plan. He saw things in people that they couldn\'t even see in themselves. That\'s what made him different. That\'s what made him irreplaceable. Rest easy, king. We\'re still learning from you.',
    image: MATT_PHOTOS.thinker,
    lat: 40.6838, lng: -73.9650,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_3',
    category: 'tribute',
    name: 'Backpack Matt — The Crown',
    subtitle: 'King of Clinton Hill',
    description: 'Nobody crowned Matt. He didn\'t need that. The streets did it on their own — not because he demanded respect, but because he earned it every single day. He treated everybody the same. From the corner boys to the elders on the stoop, Matt saw you. And when Matt saw you, you felt like somebody. Long live the king.',
    image: MATT_PHOTOS.crown,
    lat: 40.6822, lng: -73.9602,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_4',
    category: 'tribute',
    name: 'Backpack Matt — White Suit Memorial',
    subtitle: 'Golden Sun Rays',
    description: 'Matt cleaned up like nobody else. When he walked into a room, the energy shifted. He carried himself with a dignity that reminded you what it meant to hold your head high no matter what you were going through. This one\'s for the man who made us all want to be better. MATTHEW EZEKIEL FRANKLIN. 1987-2025. Shine on, brother.',
    image: MATT_PHOTOS.whiteSuit,
    lat: 40.6845, lng: -73.9595,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_5',
    category: 'tribute',
    name: 'Backpack Matt — Clinton Hill Bed-Stuy',
    subtitle: 'We Love You Matt',
    description: 'Two neighborhoods. One heart. Clinton Hill and Bed-Stuy both claim Matt because Matt claimed both of them. He didn\'t just live here — he poured into this community. Every kid he mentored, every family he looked out for, every person he lifted up when the world was pushing them down. We love you, Matt. Always will.',
    image: MATT_PHOTOS.clintonHill,
    lat: 40.6818, lng: -73.9635,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_6',
    category: 'tribute',
    name: 'Backpack Matt — The Sidewalk Shrine',
    subtitle: 'M.F. In Candles',
    description: 'They spelled out his initials in candles on the sidewalk and nobody has let them go out. Every day somebody comes by to relight them. That\'s the kind of man Matt was — even gone, he keeps people coming together. This little stretch of concrete became sacred ground the moment he left us. M.F. Forever.',
    image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/gt5mxvpr_CFDA11F8-D57D-4FE6-BD81-CDD872187F06.webp',
    lat: 40.6853, lng: -73.9610,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_7',
    category: 'tribute',
    name: 'Backpack Matt — MATT In Flowers',
    subtitle: 'Candlelight Vigil',
    description: 'The first night was the hardest. Hundreds showed up and nobody said a word for the first ten minutes. Just candles and silence and the weight of losing somebody who held everything together. Then the stories started. And the tears. And the laughter. Because Matt would have wanted the laughter. He always did.',
    image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/mbay17py_00BBE4CB-93D3-4AAE-A29A-07C9F0F0C6B3.png',
    lat: 40.6835, lng: -73.9660,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_8',
    category: 'tribute',
    name: 'Backpack Matt — Family Mural',
    subtitle: 'Father. Protector. Legend.',
    description: 'Before he was Backpack Matt to the neighborhood, he was Dad. He was the one who showed up. He was the one who stayed. Everything he built — the stories, the art, the movement — it all started with his family. They were his reason. And that love he had for them? You could feel it in everything he touched.',
    image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/daivv6n0_C3E3FEB7-F3C9-41EB-9C26-8B6034E03CD8.webp',
    lat: 40.6810, lng: -73.9580,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_9',
    category: 'tribute',
    name: 'Backpack Matt — MATT MF.',
    subtitle: 'The Night Vigil',
    description: 'Matthew Franklin. Period. No explanation needed. If you know, you know. And if you didn\'t know him, you missed out on one of the realest people Brooklyn ever produced. He created worlds with his words. He built something that\'s going to outlive all of us. That\'s not a legacy — that\'s immortality.',
    image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/u1zqfh3h_EBD9122B-B807-4F1B-9BA3-31E9C8F64487.png',
    lat: 40.6842, lng: -73.9575,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_10',
    category: 'tribute',
    name: 'Backpack Matt — Red Hoodie Wall',
    subtitle: 'Standing Tall',
    description: 'Matt never backed down and he never bent. Not for money, not for pressure, not for anything that would compromise who he was. He stood tall when it was easy and he stood taller when it wasn\'t. This tribute is for the man who showed a whole generation what it looks like to stay true to yourself no matter what comes.',
    image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/4bn2jsto_F0975F4D-F54F-4BA0-962B-BFD29A5EB88A.png',
    lat: 40.6826, lng: -73.9645,
    color: '#FFFFFF',
  },
  {
    id: 'matt_tribute_11',
    category: 'tribute',
    name: 'Backpack Matt — Lewis Ave Church Vigil',
    subtitle: 'LONG LIVE MATT',
    description: 'His church. His people. The whole community came out — hundreds deep, candles in hand, tears on faces, love in the air. This is where Matt found his peace and this is where the neighborhood came to say goodbye. But it wasn\'t really goodbye. Because Matt lives in every person he ever inspired, every story he ever told, and every block he ever walked. Long live Matt. Forever.',
    image: MATT_PHOTOS.longLiveMatt,
    lat: 40.6849, lng: -73.9356,
    color: '#FFFFFF',
  },
  // ─── ERA 1 CRIME SCENES (3) — The Foundation ───
  {
    id: 'crime_policy_bank', category: 'crime_scene', era: 1,
    name: 'The Bullies Policy Bank',
    subtitle: 'Harlem — Where The Money Started (1919)',
    description: 'Lenox Avenue, Harlem. 1919. This is where the Bullies ran their numbers operation — the policy bank that funded an empire. Before CMD, before Morellis, before any of it, there was a man with a ledger on this corner counting nickels that became millions.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/8cbf3592e35381b8504bb12ef28b4d5320aaee7cda3b47f73ae999a14cd5a3e3.jpeg', lat: 40.8095, lng: -73.9445, color: '#FF6B35',
  },
  {
    id: 'crime_pink_houses', category: 'crime_scene', era: 1,
    name: 'Pink Houses',
    subtitle: 'East New York — Where Toribio Lost Everything',
    description: 'Linden Blvd, East New York. The Pink Houses projects. This is where Lt. Toribio Rodriguez grew up, where he lost his cousins, where the Morellis first dug their claws into Brooklyn. The hallways still smell like the 90s.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/b8cb764d4d235edcf1b3127f40f329745a8ea66d7fc0ea9db8f6411c5fdeeba0.jpeg', lat: 40.6580, lng: -73.8840, color: '#FF6B35',
  },
  {
    id: 'crime_spofford', category: 'crime_scene', era: 1,
    name: 'Spofford Juvenile Center',
    subtitle: 'Where Demons Were Born — The Bronx',
    description: 'The infamous Spofford detention center in Hunts Point. Half the players in the KOW universe passed through these walls as juveniles. Ski, Toribio, half of CMD — they all did time here before they were old enough to vote. The building is gone but the energy stays.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/8428483215facbda58e9e5f6f3378581ae735d5c751c196564d83e008c251330.jpeg', lat: 40.8120, lng: -73.8870, color: '#FF6B35',
  },
  // ─── ERA 2 CRIME SCENES (7) — Crime Rising ───
  {
    id: 'crime_milford_st', category: 'crime_scene', era: 2,
    name: 'Milford Street',
    subtitle: 'The Block That Started The War',
    description: 'Milford Street, East New York. The intersection where Toribio made his choice. Morelli money flowing one way, CMD loyalty pulling the other. When the shooting started here, it did not stop for twenty years.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/ca57a476aacd31c9ea3233d9f3c1d49a3d96a11bfb4c5aa6d9dbab7e8243647c.jpeg', lat: 40.6695, lng: -73.8750, color: '#FF6B35',
  },
  {
    id: 'crime_ski_warehouse', category: 'crime_scene', era: 2,
    name: 'Ski Crew Warehouse',
    subtitle: 'East Flatbush — The Loading Dock',
    description: 'An abandoned warehouse on Utica Ave. Ski Crew stored product here, counted cash here, and settled disputes here. The loading dock faced an alley with no cameras. What went in didn\'t always come out. The building is condos now. The ghosts remain.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/93ccf851725d20b53e573dc7e4daaead9d00164fd9f3dbbfad98f1ac447449ea.jpeg', lat: 40.6490, lng: -73.9305, color: '#FF6B35',
  },
  {
    id: 'crime_cmd_hq', category: 'crime_scene', era: 2,
    name: 'CMD Headquarters',
    subtitle: 'Brownsville — Where CMD Was Born',
    description: 'A basement on Rockaway Ave. No sign. No name. Just a steel door and a handshake. This is where Coke Money District held their meetings, counted their money, and planned their expansion from Brownsville to the rest of Brooklyn. The walls heard everything.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/7bfc9322b599f5a292ed280004a28ba57d17b2f76806800527761567e7559e83.jpeg', lat: 40.6620, lng: -73.9075, color: '#FF6B35',
  },
  {
    id: 'crime_morelli_club', category: 'crime_scene', era: 2,
    name: 'Morelli Social Club',
    subtitle: 'Bensonhurst — The Family Table',
    description: 'A storefront on 18th Avenue that never changed its sign. The Morelli family ran their operation from the back room. Italian coffee, old newspapers, and conversations that decided who lived and who disappeared. The espresso machine never stopped running. Neither did the body count.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/78e8b30013ce57e43ae4229e5ef09bcaff5d60ae2660f2d6896c3c2f489a05dc.jpeg', lat: 40.6145, lng: -73.9930, color: '#FF6B35',
  },
  {
    id: 'crime_waterfront_e2', category: 'crime_scene', era: 2,
    name: 'The Brooklyn Waterfront',
    subtitle: 'Where Bodies Meet The Water',
    description: 'Red Hook. The waterfront. In the KOW universe, this is where disputes end. The East River does not give back what it takes. Every faction in Brooklyn knows: if you hear "meet me at the water," you are either making a deal or making a grave.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/d7ccdc24e9385a5dc3e6ffa6f5e5555694a2b4090f6f1db090bacb5781ffa267.jpeg', lat: 40.6785, lng: -73.9990, color: '#FF6B35',
  },
  {
    id: 'crime_butcher_shop', category: 'crime_scene', era: 2,
    name: 'The Butcher Shop Hit',
    subtitle: 'East New York — Ski Crew\'s Last Stand',
    description: 'The butcher shop on Pennsylvania Ave where Ski Crew ran their side operation. CMD hit it on a Tuesday afternoon. Three went in, two came out. The meat hooks were not just for show after that day. The block still calls it "the shop" and everybody knows what they mean.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/deb965f531e7aec04e690e24164c0942386f98e38bd38668a7d7ed0196d3584f.jpeg', lat: 40.6722, lng: -73.8955, color: '#FF6B35',
  },
  {
    id: 'crime_webster_shooting', category: 'crime_scene', era: 2,
    name: 'The Webster Hall Shooting',
    subtitle: 'Manhattan — Music Night Turned Murder Night',
    description: 'Webster Hall on East 11th. What started as a packed Friday night turned into a warzone in the VIP section. Two rival crews, one narrow staircase, and three bodies before the DJ even cut the music. The club never felt the same after.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/184a03e47b67b425edd9602a4d11ab35130a7256bf7846491fb6a0d9ee2faba0.jpeg', lat: 40.7308, lng: -73.9904, color: '#FF6B35',
  },
  // ─── ERA 3 CRIME SCENES (14) — Peak Crime, The Worst ───
  {
    id: 'crime_diego_safehouse', category: 'crime_scene', era: 3,
    name: 'Diego Safe House',
    subtitle: 'Flatbush — The Ghost Room',
    description: 'A two-bedroom apartment on Flatbush Ave that was never in anyone\'s name. Diego kept it clean — no phone, no mail, no neighbors who asked questions. When the heat came, this is where he vanished. When he left for LA, the key was under the mat. Nobody ever found it.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/0eef08f2ba8412c1f315891ddcb3321692775e80a56b5ca6ad92240bfeb86359.jpeg', lat: 40.6525, lng: -73.9591, color: '#FF6B35',
  },
  {
    id: 'crime_bushwick_deli', category: 'crime_scene', era: 3,
    name: 'The Bushwick Deli Hit',
    subtitle: 'Morelli Stash House — Cleaned Out',
    description: 'A deli on Knickerbocker Ave that sold more heroin than hero sandwiches. CMD hit the Morelli stash spot in broad daylight. Cleaned out $200K in product and left a message spray-painted on the wall. The Morellis never used that location again.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/be14f68048df495315b15d66b70cc2dc1ccce25cf5a5f7522873d84767465aa9.jpeg', lat: 40.7060, lng: -73.9230, color: '#FF6B35',
  },
  {
    id: 'crime_morelli_execution', category: 'crime_scene', era: 3,
    name: 'Ronald Morelli Execution',
    subtitle: 'Williamsburg — The Bridge Hit',
    description: 'Under the Williamsburg Bridge. Ronald Morelli was supposed to be untouchable — the old man\'s nephew, protected by blood. But blood does not stop bullets. Two shooters waited behind the support columns. He never made it to his car.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/5873a430f804fa6b6569000dfa729970548569c53dd23f6a5575ccd032855a9c.jpeg', lat: 40.7120, lng: -73.9540, color: '#FF6B35',
  },
  {
    id: 'crime_maurice_brownstone', category: 'crime_scene', era: 3,
    name: 'Maurice\'s Brownstone Retaliation',
    subtitle: 'Bed-Stuy — The Night They Came Back',
    description: 'A brownstone on Hancock Street. Maurice thought he was safe — reinforced doors, cameras, two men on the stoop. But they came through the roof. Three dead inside, the brownstone burned from the top floor down. The fire department did not rush.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/728cb00382d20dd1c225bbde8e7a24d2573c560e4edc85fbbc849e5334a2c97f.jpeg', lat: 40.6860, lng: -73.9420, color: '#FF6B35',
  },
  {
    id: 'crime_gustav_meeting', category: 'crime_scene', era: 3,
    name: 'Gustav\'s Russian Quarter',
    subtitle: 'Midtown — The Alliance That Cost Lives',
    description: 'A restaurant on 57th Street with no sign on the door. Gustav brokered the Russian alliance from booth seven. Three waiters were armed, the kitchen had a back exit to a parking garage. When the deal went sideways, four men left through that exit and were never seen again.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/ba1855b5f8504168bc59664eae5124204a8eeb614ea787851a6f20e987a958a9.jpeg', lat: 40.7645, lng: -73.9735, color: '#FF6B35',
  },
  {
    id: 'crime_friday_hit', category: 'crime_scene', era: 3,
    name: 'Friday Night Hit — Red Vans',
    subtitle: 'Bedford Ave — The Sidewalk Massacre',
    description: 'Bedford Avenue on a Friday night. Two red vans pulled up. Eight men got out. They were looking for one person and killed four instead. The bodega cameras caught everything but the faces. Nobody talked. Nobody ever talks on Bedford.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/436740632c72b386ac9979dc4eee132064cda89fd8587a5ea964aa41d95e999a.jpeg', lat: 40.6950, lng: -73.9350, color: '#FF6B35',
  },
  {
    id: 'crime_fuego_lenox', category: 'crime_scene', era: 3,
    name: 'Fuego\'s Lenox Ave Bloodbath',
    subtitle: 'Harlem — DDN\'s Last Stand',
    description: 'Lenox Avenue, 125th Street. The final strike on DDN\'s Harlem corridor. CMD came from both ends of the block. Fuego\'s crew was trapped between a barbershop and a chicken spot. Six bodies on the sidewalk by midnight. Harlem changed hands that night.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/484e65f02424a8e5635ee665f1098cc81eab0d6eae7fbe3e8d4eb4cc706b2267.jpeg', lat: 40.8096, lng: -73.9459, color: '#FF6B35',
  },
  {
    id: 'crime_princeton', category: 'crime_scene', era: 3,
    name: 'The Princeton Crisis',
    subtitle: 'New Jersey — Blood On Ivy',
    description: 'A quiet street in Princeton where quiet things happened loudly. Jordan\'s Jersey expansion hit a wall when the local connect turned informant. The meeting at the college town diner ended with two bodies in a rental car and a federal investigation that went nowhere.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/fdb297fbab091f3a0adaa234722710f20a2cdc3996c79e8aecaa087c172082f9.jpeg', lat: 40.3573, lng: -74.6672, color: '#FF6B35',
  },
  {
    id: 'crime_queens_robbery', category: 'crime_scene', era: 3,
    name: 'Queens Electronics Shootout',
    subtitle: 'Flushing — The Store That Was Not a Store',
    description: 'The electronics store in Flushing was CMD\'s laundering front. When three armed men tried to rob it, they did not know the stockroom had more guns than TVs. The shootout lasted four minutes. The store was repaired and reopened the next day.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/ef24280cba070c3edba1cacb8c1a53256c738bac5fcdd3eab3a77edacc890251.jpeg', lat: 40.7282, lng: -73.7949, color: '#FF6B35',
  },
  {
    id: 'crime_bronx_war', category: 'crime_scene', era: 3,
    name: 'Bronx Distribution War',
    subtitle: 'Demon\'s Ground — The Three-Block Massacre',
    description: 'Three blocks in the South Bronx turned into a warzone when Los Diablos tried to undercut CMD\'s distribution. Demon handled it personally. By the end of the week, Los Diablos had lost their Bronx connect, three soldiers, and all their nerve.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/3b22f1aaec02fdbf2861735fe1f7c4aae5defa3dbb690536e54f424467879097.jpeg', lat: 40.8200, lng: -73.9150, color: '#FF6B35',
  },
  {
    id: 'crime_paolo_warehouse', category: 'crime_scene', era: 3,
    name: 'Paolo\'s Warehouse Massacre',
    subtitle: 'Brooklyn — The Peruvian Connection Gone Wrong',
    description: 'A warehouse in East Flatbush. Paolo\'s Peruvian connect sent the wrong shipment and the wrong men to explain it. The meeting turned into a bloodbath. CMD lost two soldiers. The Peruvians lost five. The cocaine was never found.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/a0cfc4c19c2a788561cb4157ab495a21181e6092a797ccbc090d281fc5143a91.jpeg', lat: 40.6450, lng: -73.9600, color: '#FF6B35',
  },
  {
    id: 'crime_virginia_strip', category: 'crime_scene', era: 3,
    name: 'Virginia Beach Violence',
    subtitle: 'The Pipeline\'s Southern Endpoint',
    description: 'Virginia Beach. The end of the I-95 pipeline. CMD\'s southern distribution hub operated out of a strip mall. When a local crew tried to tax the operation, CMD sent a message that was heard all the way back to Norfolk. Three buildings, one night.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/9fd1f002d4deaf2e42cf61e1c3db8f9ef9948bc7f34c418e3609f16be96ad8a4.jpeg', lat: 36.8529, lng: -75.9780, color: '#FF6B35',
  },
  {
    id: 'crime_jersey_pipeline', category: 'crime_scene', era: 3,
    name: 'Jersey City Pipeline War',
    subtitle: 'The Tunnel Fight',
    description: 'Jersey City. The Holland Tunnel exit where CMD\'s pipeline crossed into Jersey. When Newark crews tried to control the route, the war spilled into the streets. Two car chases, one explosion at a gas station, and a body in the Hackensack River. The pipeline stayed open.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/f625e643dec83034c0a6cb406fc8afb380d6e29add53297537cd3240241730ce.jpeg', lat: 40.7178, lng: -74.0431, color: '#FF6B35',
  },
  {
    id: 'crime_summit_ambush', category: 'crime_scene', era: 3,
    name: 'Five Borough Summit Ambush',
    subtitle: 'Midtown — The Meeting Nobody Survived',
    description: 'A hotel suite in Midtown where representatives from all five boroughs came to negotiate peace. Somebody wired the elevators. The summit lasted twenty minutes before the first shots. Three borough leaders dead, the hotel evacuated, and the war continued for another two years.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/7e277375f23a2837b3fb3c2f942a17bcd2f3a361be14baa33a702f512beb8e06.jpeg', lat: 40.7580, lng: -73.9855, color: '#FF6B35',
  },
  {
    id: 'crime_la_crenshaw', category: 'crime_scene', era: 3,
    name: 'The Crenshaw Boulevard Shooting',
    subtitle: 'Los Angeles — West Coast Introduction',
    description: 'CMD\'s first attempt to establish a West Coast presence ended in gunfire on Crenshaw Boulevard. The local crews didn\'t want New York money on their streets. Two cars, six shooters, and a message that took another year and a lot more blood to rewrite. LA wasn\'t Brooklyn. The rules were different.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/db08c2da4086174081501ad24a09c5ad355f57695dc168a9b0a7c4b618a11f6e.jpeg', lat: 33.9925, lng: -118.3300, color: '#FF6B35',
  },
  {
    id: 'crime_la_inglewood', category: 'crime_scene', era: 3,
    name: 'Inglewood Warehouse Ambush',
    subtitle: 'Los Angeles — The Setup',
    description: 'A warehouse in Inglewood that was supposed to be a simple product exchange. The Peruvian connect sent the LA crew instead of the Brooklyn crew. Nobody recognized anybody. By the time the confusion cleared, three men were dead and $400K in product was on the floor. LA learned that day — CMD doesn\'t do confusion.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/528e3e07b44cbca8ba70c046a72f526f2ef2497fd4d21326ec55cce68e292b03.jpeg', lat: 33.9617, lng: -118.3531, color: '#FF6B35',
  },
  {
    id: 'crime_atlanta_bankhead', category: 'crime_scene', era: 3,
    name: 'The Bankhead Courts Hit',
    subtitle: 'Atlanta — Southern Expansion Goes Wrong',
    description: 'Bankhead Courts, Atlanta. CMD tried to open a southern distribution line through the projects. The local crews had been running those blocks for decades and weren\'t about to hand them over to New York. The shootout lasted three minutes. CMD lost two soldiers and gained respect. They came back with more men the next month.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/0aa306932255316935e248c2ac3e4a5aba40e3c55e20f639a4ac137436268003.jpeg', lat: 33.7537, lng: -84.4142, color: '#FF6B35',
  },
  {
    id: 'crime_atlanta_zone6', category: 'crime_scene', era: 3,
    name: 'Zone 6 Retaliation',
    subtitle: 'Atlanta — The Message',
    description: 'After Bankhead, CMD sent a message through Zone 6 that nobody in Atlanta forgot. Three trap houses shut down in one night, no shots fired, no bodies — just empty safes and a note that said "Next time we won\'t be polite." Atlanta opened its doors after that.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/76ef037fb6e9000c47640ce53333b425a32fe28f3c2972577189c011f13f6631.jpeg', lat: 33.7183, lng: -84.3458, color: '#FF6B35',
  },
  {
    id: 'crime_houston_ship', category: 'crime_scene', era: 3,
    name: 'Houston Ship Channel Massacre',
    subtitle: 'Texas — The Port That Ran Red',
    description: 'The Houston Ship Channel was CMD\'s gateway to Texas distribution. When a rival cartel tried to intercept a shipment at the port, the resulting firefight shut down two loading docks for a week. Five bodies were pulled from the water. Houston PD called it "gang-related" and moved on. The shipments never stopped.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/00643ebd8803c8899d22e6e2822eff59f6549cdcf79e1b8156602be45bc03b4b.jpeg', lat: 29.7604, lng: -95.3698, color: '#FF6B35',
  },
  {
    id: 'crime_dallas_deep_ellum', category: 'crime_scene', era: 3,
    name: 'Deep Ellum Double Cross',
    subtitle: 'Dallas — Trust Dies in Texas',
    description: 'A bar in Deep Ellum, Dallas. CMD\'s Texas connect arranged a meeting to expand distribution into Fort Worth. The connect brought DEA instead of product. Two CMD soldiers barely made it out the back door. The connect was found in a dumpster behind a Waffle House three days later. Nobody in Dallas crossed CMD again.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/ea9b1d3ebdbee9854f6706f8e5f5265602f3f8717c5dbc282da65f6d558785c3.jpeg', lat: 32.7767, lng: -96.7970, color: '#FF6B35',
  },
  {
    id: 'crime_omaha_pipeline', category: 'crime_scene', era: 3,
    name: 'The Omaha Pipeline Explosion',
    subtitle: 'Nebraska — Middle America Burns',
    description: 'Nobody expected CMD to reach Omaha. That was the point. A meth lab disguised as a farm equipment warehouse on the outskirts of town. When a rival crew from Kansas City tried to rob it, the resulting fire could be seen from three counties. The lab was rebuilt in a different location within a month. The Midwest belonged to CMD.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/62e8ca0dc34ebfc3b150f6613972abf700beb5f01e7df127b9e1ed2c97b0638a.jpeg', lat: 41.2565, lng: -95.9345, color: '#FF6B35',
  },
  {
    id: 'crime_ct_bridgeport', category: 'crime_scene', era: 3,
    name: 'Bridgeport CT Bloodbath',
    subtitle: 'Connecticut — The I-95 Corridor War',
    description: 'Bridgeport was supposed to be a quiet stop on the I-95 pipeline. But the local crews in Connecticut had their own supply lines and weren\'t interested in sharing. Three drive-bys in two weeks. A barbershop shot up on a Saturday afternoon. CMD eventually won the corridor, but Connecticut cost them more soldiers than any borough in New York.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/65c41e3bf7e7ba72e81e4ac2280900b48ce758c3b2ef09b56689bd27e5a45051.jpeg', lat: 41.1792, lng: -73.1894, color: '#FF6B35',
  },
  {
    id: 'crime_newark_ironbound', category: 'crime_scene', era: 3,
    name: 'Newark Ironbound Massacre',
    subtitle: 'New Jersey — The Block That Fought Back',
    description: 'The Ironbound district in Newark. Portuguese and Brazilian crews had run this neighborhood for years. When CMD tried to set up shop on Ferry Street, the response was immediate and brutal. A restaurant was firebombed. Two cars were torched. CMD had to negotiate a truce that split the district down the middle. Some blocks you don\'t take — you share.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/ea0306870ffdddd343704cb9472d6d42229303775f6a566081c717169a274d46.jpeg', lat: 40.7357, lng: -74.1553, color: '#FF6B35',
  },
  {
    id: 'crime_va_norfolk', category: 'crime_scene', era: 3,
    name: 'Norfolk Docks Execution',
    subtitle: 'Virginia — The Naval Connection',
    description: 'The Norfolk docks were CMD\'s southern port. A Navy enlisted man had been moving product through the base for two years before he got greedy and tried to skim. They found him in a shipping container a week later. The Navy connection was replaced within 48 hours. The pipeline south never missed a beat.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/45506d9d8f6c787d30a2a3c9675cb1d2a14aebb317b8fd1e63d1609a86a09501.jpeg', lat: 36.8508, lng: -76.2859, color: '#FF6B35',
  },
  // ─── ERA 4 CRIME SCENES (9) — Dying Down But Still Bad ───
  {
    id: 'crime_sing_sing', category: 'crime_scene', era: 4,
    name: 'Sing Sing Yard Attack',
    subtitle: 'Ossining — Behind The Walls',
    description: 'The yard at Sing Sing. Even behind bars, the war continued. A sharpened piece of metal, a blind spot in the camera coverage, and a score that had waited three years to be settled. The guards took twelve minutes to respond. That was eleven minutes too many.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/a1fbd2c58ee92257985f201f24ee405967151edd660452b65670a436ddbcc3ab.jpeg', lat: 41.1540, lng: -73.7860, color: '#FF6B35',
  },
  {
    id: 'crime_waterfront_execution', category: 'crime_scene', era: 4,
    name: 'The Waterfront Execution',
    subtitle: 'Red Hook — History Repeating',
    description: 'Red Hook docks. Again. Different decade, same water. Tito thought he could control the new waterfront operations without paying respects. They found him floating face down by Pier 11 on a Sunday morning. The fishermen said they did not see anything.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/fc3ce8cf545e63d5ccdfcfaa335a377581bda9b71ae35ac94e837e20e83d1c2d.jpeg', lat: 40.6775, lng: -74.0090, color: '#FF6B35',
  },
  {
    id: 'crime_casino_massacre', category: 'crime_scene', era: 4,
    name: 'Vincent\'s Casino Night Massacre',
    subtitle: 'Manhattan — The Final Play',
    description: 'The underground casino on the Lower East Side. Vincent Morelli thought he was hosting a high-stakes poker night. He was hosting his own funeral. The power went out at 11:47 PM. When it came back on, three men were dead at the table and Vincent was gone.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/4ff788addaed29153343440cd71496e1a0b4686ff3ee2357630a2dbc07f6339f.jpeg', lat: 40.7128, lng: -73.9680, color: '#FF6B35',
  },
  {
    id: 'crime_rodriguez_precinct', category: 'crime_scene', era: 4,
    name: 'Rodriguez\'s Precinct',
    subtitle: 'The Bronx — Where The Law Broke',
    description: 'Commissioner Rodriguez\'s precinct in the Bronx. The ledger that exposed decades of corruption was taken from the third floor filing room. Two officers were found unconscious, the security footage was wiped, and Rodriguez resigned by morning.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/ed2368218ce9a5c4429eda18e18bf0b61da4c9a49d4523bb5d9e8aca6cd8a9a5.jpeg', lat: 40.8375, lng: -73.8685, color: '#FF6B35',
  },
  {
    id: 'crime_chinatown_war', category: 'crime_scene', era: 4,
    name: 'Chinatown Red Serpent War',
    subtitle: 'Manhattan — The Dragon\'s Bite',
    description: 'Canal Street, Chinatown. The Red Serpents controlled the gambling dens and the laundromats. When CMD tried to negotiate, Chuck Wong sent back the messenger in pieces. The retribution was surgical — four businesses closed in one night, no bodies found, no witnesses.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/2ef8f6d3aa8840ded5433ca9e00b5f2afea04c24a0865fe46598ffb263ce2342.jpeg', lat: 40.7158, lng: -73.9970, color: '#FF6B35',
  },
  {
    id: 'crime_tito_dock', category: 'crime_scene', era: 4,
    name: 'Tito\'s Dock Confrontation',
    subtitle: 'Sunset Park — The Betrayal',
    description: 'The loading docks at Sunset Park. Tito brought twelve men. Only eight walked away. The confrontation over waterfront control lasted forty-five minutes in the rain. When it was over, Tito\'s crew controlled nothing and owed everything.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/88a0a29d0d00295bf324ca7d5cc31e18a7ce695a1be466314ddc71c6a2fcb2a5.jpeg', lat: 40.6468, lng: -74.0108, color: '#FF6B35',
  },
  {
    id: 'crime_rosa_betrayal', category: 'crime_scene', era: 4,
    name: 'Rosa\'s Network Collapse',
    subtitle: 'Sunset Park — The Intelligence Leak',
    description: 'Rosa ran the tightest intelligence network in Brooklyn from a beauty salon on 5th Avenue. When one of her informants flipped, the whole network unraveled in 48 hours. Three safe houses exposed, two operatives arrested, and Rosa disappeared into the wind.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/21bd32ec0917de490a2515ce5ffcb1a70cf8fbe291d801646805fdb23d4c129f.jpeg', lat: 40.6455, lng: -74.0125, color: '#FF6B35',
  },
  {
    id: 'crime_jacob_office', category: 'crime_scene', era: 4,
    name: 'Jacob Hal\'s Office',
    subtitle: 'Brooklyn Heights — The Money Trail',
    description: 'A law office on Montague Street. Jacob Hal laundered money for everyone — CMD, Morellis, independents. When the feds came, he swallowed a USB drive and jumped from the third floor. He survived the fall. The drive did not survive the acid.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/71dc55a172bce1a5c9451c8cc0ffe4c067eb7d4d0434b79854c910c613a76fbe.jpeg', lat: 40.6935, lng: -73.9890, color: '#FF6B35',
  },
  {
    id: 'crime_final_strike', category: 'crime_scene', era: 4,
    name: 'The East River Strike',
    subtitle: 'Manhattan — The Last War',
    description: 'The East River pier where it all ended. Vincent\'s last loyalists made their stand on a decommissioned ferry. The assault came from the water and from the bridge above. When the sun came up, the ferry was sinking, the war was over, and Brooklyn could finally breathe.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/396bcf03408c40311caec6ebb7ef9f0dbcfed90a0aaa7ec8c2550062c5d75633.jpeg', lat: 40.7130, lng: -73.9680, color: '#FF6B35',
  },
  {
    id: 'crime_la_hollywood', category: 'crime_scene', era: 4,
    name: 'Hollywood Hills Cleanup',
    subtitle: 'Los Angeles — Ghosts Don\'t Leave Witnesses',
    description: 'A mansion in the Hollywood Hills where the West Coast money was laundered through film production companies. When the operation was compromised, the house burned to the ground in the middle of the night. The fire department found nothing but ash and melted hard drives. The insurance company paid out. Nobody asked questions.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/59bcc18bd35bf2459630e12e9771f6b36e06aba96710fad85689fd290bac69d1.jpeg', lat: 34.1010, lng: -118.3260, color: '#FF6B35',
  },
  {
    id: 'crime_atlanta_modern', category: 'crime_scene', era: 4,
    name: 'Atlanta Airport Intercept',
    subtitle: 'Georgia — The Digital Trail',
    description: 'Hartsfield-Jackson. The busiest airport in the world was also CMD\'s busiest money corridor. When a courier got flagged by TSA with $2 million in a carry-on, the digital cleanup operation that followed wiped three bank accounts, two shell companies, and one person who knew too much. Atlanta\'s modern empire ran cleaner after that.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/13223d03dd1553a2d0436232bce7bdd3e9bfd2b66f7d8b7da53af179a3aa604f.jpeg', lat: 33.6407, lng: -84.4277, color: '#FF6B35',
  },
  {
    id: 'crime_houston_modern', category: 'crime_scene', era: 4,
    name: 'Houston Energy Corridor Hit',
    subtitle: 'Texas — Oil Money Meets Street Money',
    description: 'An office building in the Energy Corridor where CMD\'s Texas operation had gone corporate. Shell companies inside shell companies. When a forensic accountant got too close, his car exploded in the parking garage. Houston PD ruled it a gas leak. The accountant\'s files disappeared from the cloud the same hour.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/49f1a4758ad5f5c9ed1e7a82fd9dd8e00499dbe0dec9d6539551d090897a3581.jpeg', lat: 29.7604, lng: -95.3698, color: '#FF6B35',
  },
  {
    id: 'crime_ct_hartford', category: 'crime_scene', era: 4,
    name: 'Hartford Network Collapse',
    subtitle: 'Connecticut — The Domino Effect',
    description: 'Hartford, Connecticut. One informant brought down an entire distribution network that had been running clean for six years. Fourteen arrests in one night across three cities. CMD\'s Connecticut operation went dark for eight months before rebuilding from scratch with all new faces. The informant was never found.',
    image: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/5f27576e6921252395cbbdf53146b326e15252747b2e561ed50fffe562f4874a.jpeg', lat: 41.7658, lng: -72.6734, color: '#FF6B35',
  },
];

const LEGEND_CATEGORIES = {
  tribute: { label: 'TRIBUTES', color: '#FFFFFF', icon: '🕯' },
  crime_scene: { label: 'CRIME SCENES', color: '#FF6B35', icon: '🔪' },
};

// Candle-shaped SVG marker for tributes
const createCandleIcon = () => L.divIcon({
  className: 'tribute-candle-icon',
  iconSize: [18, 28],
  iconAnchor: [9, 28],
  html: `<svg width="18" height="28" viewBox="0 0 32 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="10" r="10" fill="#FFD700" opacity="0.12">
      <animate attributeName="r" values="10;14;10" dur="1.5s" repeatCount="indefinite"/>
    </circle>
    <path d="M16 2 C16 2, 22 10, 20 15 C19 17, 13 17, 12 15 C10 10, 16 2, 16 2Z" fill="#FF6B00" opacity="0.9">
      <animate attributeName="d" values="M16 2 C16 2,22 10,20 15 C19 17,13 17,12 15 C10 10,16 2,16 2Z;M16 4 C16 4,20 9,19 13 C18 15,14 15,13 13 C12 9,16 4,16 4Z;M16 2 C16 2,22 10,20 15 C19 17,13 17,12 15 C10 10,16 2,16 2Z" dur="0.9s" repeatCount="indefinite"/>
    </path>
    <path d="M16 6 C16 6, 19 11, 18 14 C17.5 15, 14.5 15, 14 14 C13 11, 16 6, 16 6Z" fill="#FFCC00">
      <animate attributeName="d" values="M16 6 C16 6,19 11,18 14 C17.5 15,14.5 15,14 14 C13 11,16 6,16 6Z;M16 7 C16 7,18 10,17.5 13 C17 14,15 14,14.5 13 C14 10,16 7,16 7Z;M16 6 C16 6,19 11,18 14 C17.5 15,14.5 15,14 14 C13 11,16 6,16 6Z" dur="0.9s" repeatCount="indefinite"/>
    </path>
    <line x1="16" y1="15" x2="16" y2="20" stroke="#3D2B1F" stroke-width="2" stroke-linecap="round"/>
    <rect x="10" y="20" width="12" height="24" rx="2.5" fill="#F5F0E6" stroke="#C9B896" stroke-width="1"/>
    <path d="M12 20 Q11.5 24, 10 26 L10 20Z" fill="#EAE0CE"/>
    <path d="M20 20 Q20.5 23, 22 25 L22 20Z" fill="#EAE0CE"/>
    <rect x="8" y="43" width="16" height="5" rx="2.5" fill="#C9B896" stroke="#A89770" stroke-width="0.5"/>
  </svg>`,
});

// Dot icon for job/territory markers (replaces CircleMarker for clustering)
const createDotIcon = (fillColor, strokeColor, radius, opacity = 0.85, weight = 2) => {
  const size = Math.max(radius * 2, 6);
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${fillColor};border:${weight}px solid ${strokeColor};opacity:${opacity};box-shadow:0 0 4px ${fillColor}30;"></div>`,
  });
};

// Crime scene dot icon
const createCrimeIcon = (zoom) => {
  const size = zoom >= 14 ? 20 : zoom >= 12 ? 16 : 12;
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#FF6B35;border:2.5px solid #050505;opacity:0.9;box-shadow:0 0 6px #FF6B3530;"></div>`,
  });
};

// Cluster icon factory
const createClusterIcon = (color) => (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 20 ? 44 : count >= 10 ? 38 : 32;
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    html: `<div data-testid="marker-cluster" style="width:${size}px;height:${size}px;border-radius:50%;background:${color}20;border:2px solid ${color}60;color:${color};display:flex;align-items:center;justify-content:center;font-family:Outfit,sans-serif;font-size:${count >= 20 ? 14 : 12}px;font-weight:700;backdrop-filter:blur(4px);box-shadow:0 0 12px ${color}30;">${count}</div>`,
  });
};

// Share tribute function
const shareTribute = async (legend) => {
  const shareData = {
    title: legend.name,
    text: `${legend.name} — ${legend.subtitle}. ${legend.description}`,
    url: window.location.href,
  };
  try {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(`${legend.name}\n${legend.subtitle}\n\n${legend.description}\n\n${window.location.href}`);
      alert('Copied to clipboard');
    }
  } catch (e) { /* user cancelled share */ }
};

// Legend Detail Panel — FULL SCREEN photo viewer
const LegendDetailPanel = ({ legend, onClose }) => {
  if (!legend) return null;
  const cat = LEGEND_CATEGORIES[legend.category] || LEGEND_CATEGORIES.tribute;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-[1002] flex flex-col"
      style={{ background: '#0a0c10f5', backdropFilter: 'blur(20px)' }}
      data-testid="legend-detail-panel"
    >
      {/* Close bar */}
      <div className="flex items-center justify-between p-4 shrink-0">
        <span className="px-2 py-0.5 rounded text-[8px] font-bold tracking-widest font-['Outfit']"
          style={{ background: `${legend.color}15`, color: legend.color, border: `1px solid ${legend.color}30` }}>
          {cat.label}
        </span>
        <button onClick={onClose} className="text-[#E8E0D0]/40 hover:text-[#E8E0D0] px-3 py-1 rounded-lg border border-[#1A2535] font-['Outfit'] text-[10px] tracking-widest" data-testid="legend-detail-close">
          CLOSE
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-4">
        {/* LARGE photo — fills most of the screen */}
        {legend.image ? (
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: legend.color + '20' }}>
            <img src={legend.image} alt={legend.name} className="w-full object-contain bg-[#050505]" style={{ maxHeight: '60vh' }} loading="lazy" data-testid="legend-photo" />
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden border border-[#1A2535] flex flex-col items-center justify-center py-16 bg-[#0a0c10]" data-testid="crime-scene-photo-placeholder">
            <Crosshair size={32} className="text-[#FF6B35]/40 mb-3" />
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs tracking-widest">PHOTO PENDING</p>
            <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[9px] mt-1">Supply your own location photo</p>
          </div>
        )}

        {/* Info */}
        <div>
          <h4 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0]">{legend.name}</h4>
          <p className="font-['Outfit'] text-[#E8E0D0]/40 text-sm italic mt-1">{legend.subtitle}</p>
        </div>

        <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm leading-relaxed">{legend.description}</p>

        {/* Share button — tributes only */}
        {legend.category === 'tribute' && (
          <button
            onClick={() => shareTribute(legend)}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border transition-all hover:brightness-125"
            style={{ borderColor: legend.color + '30', background: legend.color + '10', color: legend.color }}
            data-testid="tribute-share-btn"
          >
            <Share2 size={16} />
            <span className="font-['Outfit'] text-sm tracking-widest">SHARE THIS TRIBUTE</span>
          </button>
        )}
      </div>
    </motion.div>
  );
};

const MapLegend = ({ mode, showLegends, onToggleLegends, legendFilter, onSetFilter }) => {
  const [open, setOpen] = useState(false);
  const items = mode === 'jobs'
    ? [
      { color: '#C9A84C', label: 'Story Mission' },
      { color: '#4A7AB5', label: 'Common Hustle' },
      { color: '#9B59B6', label: 'Uncommon' },
      { color: '#E8445A', label: 'Rare' },
      { color: '#8B1A1A', label: 'Boss / Special' },
      { color: '#555', label: 'Locked' },
    ]
    : [
      { color: '#4ADE80', label: 'Owned' },
      { color: '#4A7AB5', label: 'Available' },
      { color: '#8B1A1A', label: 'Boss Block' },
      { color: '#22D3EE', label: 'Boss (Owned)' },
      { color: '#555', label: 'Locked' },
    ];

  return (
    <div className="absolute top-2 right-2 z-[1000]">
      <button onClick={() => setOpen(!open)}
        className={`bg-[#0a0c10]/90 backdrop-blur-md border rounded-lg px-3 py-1.5 text-[9px] font-['Outfit'] tracking-widest transition-all ${
          showLegends ? 'border-[#C9A84C]/40 text-[#C9A84C]' : 'border-[#1A2535] text-[#E8E0D0]/40 hover:text-[#E8E0D0]/70'
        }`}
        data-testid="map-legend-toggle">
        LEGENDS {showLegends ? 'ON' : 'OFF'}
      </button>
      {open && (
        <div className="absolute top-9 right-0 bg-[#0a0c10]/95 backdrop-blur-md border border-[#1A2535] rounded-lg p-3 space-y-1.5 min-w-[150px]">
          {/* Toggle on/off */}
          <button onClick={onToggleLegends}
            className={`w-full text-left px-2 py-1 rounded text-[9px] font-['Outfit'] tracking-widest transition-all ${
              showLegends ? 'bg-[#C9A84C]/15 text-[#C9A84C]' : 'text-[#E8E0D0]/30 hover:text-[#E8E0D0]/50'
            }`} data-testid="legends-toggle">
            {showLegends ? 'HIDE LEGENDS' : 'SHOW LEGENDS'}
          </button>
          <div className="border-t border-[#1A2535] my-1" />
          {items.map(it => (
            <div key={it.label} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: it.color }} />
              <span className="text-[#E8E0D0]/50 text-[9px] font-['Outfit']">{it.label}</span>
            </div>
          ))}
          {showLegends && (
            <>
              <div className="border-t border-[#1A2535] my-1" />
              <div className="flex items-center gap-2">
                <svg width="10" height="16" viewBox="0 0 30 48" fill="none"><path d="M15 2 C15 2, 20 8, 18 12 C17 14, 13 14, 12 12 C10 8, 15 2, 15 2Z" fill="#FF8C00" opacity="0.8"/><path d="M15 5 C15 5, 18 9, 17 11 C16.5 12, 13.5 12, 13 11 C12 9, 15 5, 15 5Z" fill="#FFD700"/><line x1="15" y1="12" x2="15" y2="17" stroke="#4A3728" strokeWidth="1.5"/><rect x="9" y="17" width="12" height="24" rx="2" fill="#F5F0E8" stroke="#D4C9B5" strokeWidth="0.8"/><rect x="7" y="39" width="16" height="4" rx="2" fill="#D4C9B5"/></svg>
                <span className="text-white/70 text-[9px] font-['Outfit']">Tribute</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border border-[#050505]" style={{ background: '#FF6B35' }} />
                <span className="text-[9px] font-['Outfit']" style={{ color: '#FF6B35' }}>Crime Scene</span>
              </div>
              <div className="border-t border-[#1A2535] my-1" />
              <p className="text-[#E8E0D0]/25 text-[7px] font-['Outfit'] tracking-widest mb-1">FILTER</p>
              <div className="flex gap-1">
                {[
                  { id: 'all', label: 'ALL' },
                  { id: 'tribute', label: 'TRIB' },
                  { id: 'crime_scene', label: 'CRIME' },
                ].map(f => (
                  <button key={f.id} onClick={() => onSetFilter(f.id)}
                    className={`px-1.5 py-0.5 rounded text-[7px] font-['Outfit'] tracking-widest transition-all ${
                      legendFilter === f.id ? 'bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C]/30' : 'text-[#E8E0D0]/30 border border-[#1A2535] hover:text-[#E8E0D0]/50'
                    }`} data-testid={`filter-${f.id}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// MAIN STORY MODE MAP
// =============================================
const StoryModeMap = ({
  mode = 'jobs',
  era = 1,
  jobs = [],
  rotatingJobs = [],
  territories = [],
  onDoJob,
  onClaimTerritory,
  onCollectTerritory,
  loading = false,
  streakCount = 0,
  rotationRefresh = 0,
  territoryOwned = 0,
  territoryNeeded = 0,
  pendingTerritory = null,
}) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobIsStory, setSelectedJobIsStory] = useState(false);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [selectedLegend, setSelectedLegend] = useState(null);
  const [showLegends, setShowLegends] = useState(true);
  const [legendFilter, setLegendFilter] = useState('all');
  const [zoom, setZoom] = useState(ERA_MAP_CONFIG[era]?.zoom || 12);
  const [flyTarget, setFlyTarget] = useState(null);
  const [takeoverFlash, setTakeoverFlash] = useState(null); // { name, color }

  const config = ERA_MAP_CONFIG[era] || ERA_MAP_CONFIG[1];

  // Combine story + rotating jobs for the map
  const allJobs = useMemo(() => {
    const storyJobs = (jobs || []).filter(j => !j.completed).map(j => ({ ...j, _isStory: true }));
    const hustles = (rotatingJobs || []).map(j => ({ ...j, _isStory: false }));
    return [...storyJobs, ...hustles];
  }, [jobs, rotatingJobs]);

  const completedJobs = useMemo(() => (jobs || []).filter(j => j.completed), [jobs]);

  // Filter legends: tributes are Easter eggs (only visible zoomed in), crime scenes are era-specific
  const filteredLegends = useMemo(() => {
    return LEGENDS_DATA.filter(l => {
      if (l.category === 'tribute' && zoom < 13) return false;
      if (l.category === 'crime_scene' && l.era !== era) return false;
      if (legendFilter !== 'all' && l.category !== legendFilter) return false;
      return true;
    });
  }, [era, legendFilter, zoom]);

  const handleJobClick = useCallback((job, isStory) => {
    setSelectedJob(job);
    setSelectedJobIsStory(isStory);
    setSelectedTerritory(null);
  }, []);

  const handleTerritoryClick = useCallback((t) => {
    setSelectedTerritory(t);
    setSelectedJob(null);
  }, []);

  const handleDoJob = useCallback((jobId) => {
    if (onDoJob) onDoJob(jobId);
    setSelectedJob(null);
  }, [onDoJob]);

  const handleClaim = useCallback((tId) => {
    // Trigger takeover flash animation
    const t = (territories || []).find(t => t.id === tId);
    if (t) {
      const coords = getCoords(tId, era);
      setFlyTarget({ center: coords, zoom: 15 });
      setTakeoverFlash({ name: t.name, color: '#4ADE80' });
      setTimeout(() => setTakeoverFlash(null), 3000);
    }
    if (onClaimTerritory) onClaimTerritory(tId);
    setSelectedTerritory(null);
  }, [onClaimTerritory, territories, era]);

  const handleCollect = useCallback((tId) => {
    if (onCollectTerritory) onCollectTerritory(tId);
    setSelectedTerritory(null);
  }, [onCollectTerritory]);

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-[#1A2535]/60" style={{ height: '520px' }} data-testid="story-mode-map">
      {/* Header bar */}
      <div className="absolute top-0 left-0 right-0 z-[1000] bg-[#0a0c10]/80 backdrop-blur-md border-b border-[#1A2535]/50 px-3 py-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={12} className="text-[#C9A84C]" />
          <span className="font-['Outfit'] text-[#E8E0D0]/60 text-[10px] tracking-widest">
            {mode === 'jobs' ? 'OPERATIONS MAP' : 'TERRITORY MAP'} — ERA {era}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {mode === 'jobs' && streakCount >= 3 && (
            <div className="flex items-center gap-1">
              <Flame size={10} className="text-[#C9A84C]" />
              <span className="font-['Outfit'] text-[#C9A84C] text-[9px]">STREAK x{streakCount}</span>
            </div>
          )}
          {mode === 'jobs' && (
            <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">
              {allJobs.length} ops · {completedJobs.length} done
            </span>
          )}
          {mode === 'territory' && (
            <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">
              {territoryOwned}/{territoryNeeded} blocks to boss
            </span>
          )}
        </div>
      </div>

      {/* COLLECT ALL banner for territory */}
      {mode === 'territory' && pendingTerritory && (pendingTerritory.cash > 0 || pendingTerritory.xp > 0) && (
        <div className="absolute top-9 left-0 right-0 z-[1000] px-2">
          <button
            onClick={() => onCollectTerritory && onCollectTerritory()}
            disabled={loading}
            className="w-full bg-[#0a0c10]/90 backdrop-blur-md border border-[#C9A84C]/30 rounded-lg px-3 py-2 flex items-center justify-between hover:bg-[#C9A84C]/10 transition-colors disabled:opacity-50"
            data-testid="collect-all-territory-btn"
          >
            <div className="flex items-center gap-2">
              <Banknote size={14} className="text-[#C9A84C]" />
              <span className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest">PENDING</span>
              <span className="font-['Outfit'] text-[#4ADE80] text-xs font-bold">+${pendingTerritory.cash?.toLocaleString()}</span>
              <span className="font-['Outfit'] text-[#4A7AB5] text-xs">+{pendingTerritory.xp} XP</span>
              {pendingTerritory.respect > 0 && (
                <span className="font-['Outfit'] text-[#E8E0D0]/40 text-xs">+{pendingTerritory.respect} Respect</span>
              )}
            </div>
            <span className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest font-bold">
              {loading ? '...' : 'COLLECT ALL'}
            </span>
          </button>
        </div>
      )}

      <MapLegend mode={mode} showLegends={showLegends} onToggleLegends={() => setShowLegends(p => !p)} legendFilter={legendFilter} onSetFilter={setLegendFilter} />

      <MapContainer
        center={config.center}
        zoom={config.zoom}
        minZoom={2}
        maxBounds={[[-85, -180], [85, 180]]}
        maxBoundsViscosity={1.0}
        worldCopyJump={false}
        style={{ height: '100%', width: '100%', background: '#060a10' }}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer url={DARK_TILES} attribution={DARK_TILES_ATTR} noWrap={true} />
        <ZoomTracker onZoomChange={setZoom} />
        {flyTarget && <FlyTo center={flyTarget.center} zoom={flyTarget.zoom} />}
        <FilterZoomController legendFilter={legendFilter} showLegends={showLegends} eraConfig={config} />

        {/* === JOBS MODE === */}
        {mode === 'jobs' && allJobs.map(job => {
              const coords = getCoords(job.id, era);
              const colors = getJobColor(job, job._isStory);
              const canDo = job.can_afford && job.level_ok && job.has_energy && !job.completed;
              const isSpecial = job.special === 'boss_confrontation' || job.special === 'respect_boost' || job.tier === 'rare';
              const r = getRadius(zoom, isSpecial);
              const icon = createDotIcon(colors.fill, colors.stroke, r, canDo ? 0.85 : 0.3, canDo ? 2 : 1);
              return (
                <Marker key={job.id} position={coords} icon={icon}
                  eventHandlers={{ click: () => handleJobClick(job, job._isStory) }}>
                  {zoom >= 13 && (
                    <Popup className="dark-popup" closeButton={false} autoPan={false}>
                      <div className="bg-[#0a0c10] border border-[#1A2535] rounded-lg p-2 min-w-[150px]" style={{ margin: '-10px -16px' }}>
                        <p className="font-['Outfit'] text-[8px] tracking-widest mb-1" style={{ color: colors.fill }}>
                          {job._isStory ? 'STORY' : (job.tier || 'HUSTLE').toUpperCase()}
                        </p>
                        <p className="font-['Outfit'] text-[#E8E0D0] text-xs font-bold">{job.name}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-[#4ADE80] text-[10px] font-['Outfit']">+${job.reward?.toLocaleString()}</span>
                          <span className="text-[#4A7AB5] text-[10px] font-['Outfit']">+{job.xp}XP</span>
                        </div>
                      </div>
                    </Popup>
                  )}
                </Marker>
              );
            })}
            {completedJobs.map(job => {
              const coords = getCoords(job.id, era);
              const r = getRadius(zoom, false) * 0.7;
              const icon = createDotIcon('#1A2535', '#2A3545', r, 0.3, 1);
              return (
                <Marker key={`done-${job.id}`} position={coords} icon={icon}>
                  {zoom >= 14 && (
                    <Popup className="dark-popup" closeButton={false} autoPan={false}>
                      <div className="bg-[#0a0c10] border border-[#1A2535] rounded-lg p-2" style={{ margin: '-10px -16px' }}>
                        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">{job.name}</p>
                        <p className="font-['Outfit'] text-[#4ADE80]/30 text-[9px]">COMPLETED</p>
                      </div>
                    </Popup>
                  )}
                </Marker>
              );
            })}
        {/* === TERRITORY MODE === */}
        {mode === 'territory' && (territories || []).map(t => {
              const coords = getCoords(t.id, era);
              const colors = getTerritoryColor(t);
              const isImportant = t.is_boss_block || t.owned;
              const r = getRadius(zoom, isImportant);
              const icon = createDotIcon(
                colors.fill, colors.stroke, r,
                t.owned ? 0.85 : t.dev_status === 'in_development' ? 0.15 : 0.5,
                t.owned ? 2.5 : t.is_boss_block ? 2 : 1
              );
              return (
                <Marker key={t.id} position={coords} icon={icon}
                  eventHandlers={{ click: () => handleTerritoryClick(t) }}>
                  {zoom >= 13 && (
                    <Popup className="dark-popup" closeButton={false} autoPan={false}>
                      <div className="bg-[#0a0c10] border border-[#1A2535] rounded-lg p-2 min-w-[140px]" style={{ margin: '-10px -16px' }}>
                        <p className="font-['Outfit'] text-[8px] tracking-widest mb-1" style={{ color: colors.fill }}>
                          {t.owned ? 'OWNED' : t.is_boss_block ? 'BOSS BLOCK' : 'UNCLAIMED'}
                        </p>
                        <p className="font-['Outfit'] text-[#E8E0D0] text-xs font-bold">{t.name}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-[#4ADE80] text-[10px] font-['Outfit']">${t.cash_per_cycle?.toLocaleString()}/cycle</span>
                          <span className="text-[#E8E0D0]/30 text-[10px] font-['Outfit']">${t.cost?.toLocaleString()}</span>
                        </div>
                      </div>
                    </Popup>
                  )}
                </Marker>
              );
            })}
        {/* === LEGENDS LAYER (tributes always, crime scenes era-filtered) === */}
        {showLegends && filteredLegends.map(legend => {
              const isTribute = legend.category === 'tribute';
              return (
                <Marker
                  key={legend.id}
                  position={[legend.lat, legend.lng]}
                  icon={isTribute ? createCandleIcon() : createCrimeIcon(zoom)}
                  eventHandlers={{
                    click: () => { setSelectedLegend(legend); setSelectedJob(null); setSelectedTerritory(null); },
                  }}
                >
                  {!isTribute && zoom >= 13 && (
                    <Popup className="dark-popup" closeButton={false} autoPan={false}>
                      <div className="bg-[#0a0c10] border rounded-lg p-2 min-w-[160px]" style={{ margin: '-10px -16px', borderColor: legend.color + '40' }}>
                        <p className="font-['Outfit'] text-[8px] tracking-widest mb-1" style={{ color: legend.color }}>
                          {LEGEND_CATEGORIES[legend.category]?.label || 'LEGEND'}
                        </p>
                        <p className="font-['Outfit'] text-[#E8E0D0] text-xs font-bold">{legend.name}</p>
                        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-0.5">{legend.subtitle}</p>
                      </div>
                    </Popup>
                  )}
                </Marker>
              );
            })}
      </MapContainer>

      {/* Territory Takeover Flash Animation */}
      <AnimatePresence>
        {takeoverFlash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 z-[999] pointer-events-none flex items-center justify-center"
            data-testid="territory-takeover-flash"
          >
            {/* Radial pulse flash */}
            <motion.div
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.3, 0.15, 0] }}
              transition={{ duration: 2, times: [0, 0.1, 0.4, 1] }}
              style={{ background: `radial-gradient(circle at center, ${takeoverFlash.color}40, transparent 70%)` }}
            />
            {/* Expanding ring */}
            <motion.div
              className="absolute rounded-full border-2"
              style={{ borderColor: takeoverFlash.color }}
              initial={{ width: 20, height: 20, opacity: 0.8 }}
              animate={{ width: 400, height: 400, opacity: 0 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            />
            <motion.div
              className="absolute rounded-full border"
              style={{ borderColor: takeoverFlash.color + '60' }}
              initial={{ width: 20, height: 20, opacity: 0.5 }}
              animate={{ width: 300, height: 300, opacity: 0 }}
              transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
            />
            {/* Territory claimed text */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.95], y: [10, 0, 0, -10] }}
              transition={{ duration: 2.5, times: [0, 0.15, 0.7, 1] }}
              className="relative text-center px-6 py-4 rounded-xl"
              style={{ background: 'rgba(5,5,5,0.85)', backdropFilter: 'blur(8px)', border: `1px solid ${takeoverFlash.color}40` }}
            >
              <p className="font-['Outfit'] text-[9px] tracking-[0.4em]" style={{ color: takeoverFlash.color }}>TERRITORY CLAIMED</p>
              <p className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0] mt-1">{takeoverFlash.name}</p>
              <motion.div className="w-16 h-px mx-auto mt-2" style={{ background: takeoverFlash.color }}
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.5, delay: 0.3 }} />
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px] mt-1">Your turf now.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom detail panels */}
      <AnimatePresence>
        {selectedLegend && (
          <LegendDetailPanel legend={selectedLegend} onClose={() => setSelectedLegend(null)} />
        )}
        {mode === 'jobs' && selectedJob && !selectedLegend && (
          <JobDetailPanel
            job={selectedJob}
            isStory={selectedJobIsStory}
            onDoJob={handleDoJob}
            onClose={() => setSelectedJob(null)}
            loading={loading}
          />
        )}
        {mode === 'territory' && selectedTerritory && !selectedLegend && (
          <TerritoryDetailPanel
            territory={selectedTerritory}
            onClaim={handleClaim}
            onCollect={handleCollect}
            onClose={() => setSelectedTerritory(null)}
            loading={loading}
          />
        )}
      </AnimatePresence>

      {/* Rotation timer (jobs mode) */}
      {mode === 'jobs' && rotationRefresh > 0 && (
        <div className="absolute bottom-2 left-2 z-[999] bg-[#0a0c10]/80 backdrop-blur-md border border-[#1A2535]/50 rounded-lg px-2 py-1">
          <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest">
            HUSTLES ROTATE: {Math.floor(rotationRefresh / 60)}:{String(rotationRefresh % 60).padStart(2, '0')}
          </span>
        </div>
      )}
    </div>
  );
};

export default StoryModeMap;
